import { useCallback, useEffect, useMemo, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { createEditor, type EditorState, type LexicalEditor } from 'lexical';
import { Toolbar } from './Toolbar';
import { ScriptScaffold } from './scriptTemplate';
import { DocHygienePlugin } from './DocHygienePlugin';
import { ListTabIndentPlugin } from './ListTabIndentPlugin';
import { ScriptShortcutsPlugin } from './ScriptShortcutsPlugin';
import {
  SCRIPT_DOC_NAMESPACE,
  SCRIPT_DOC_NODES,
  scriptDocTheme,
  SCRIPT_TITLE_FIRST_CHILD_CLASSES,
  type ScriptDoc,
} from './config';

const EDITOR_ARIA_LABEL = 'Script body';

/** Serializes an editor state to the stored doc shape. */
const toDoc = (state: EditorState): ScriptDoc => state.toJSON() as unknown as ScriptDoc;

const rethrow = (e: Error): never => {
  throw e;
};

/** Returns the seed doc as the serialized state LexicalComposer expects, or
 *  undefined when Lexical cannot parse it (unknown node type, truncated tree).
 *  LexicalComposer parses `editorState` during render, outside any error
 *  boundary, so an unreadable seed would otherwise take the whole modal down.
 *  Degrading to an empty editor is the right failure mode for a draft. */
function parseableSeedState(doc: ScriptDoc | null | undefined): string | undefined {
  if (!doc) return undefined;
  const serialized = JSON.stringify(doc);
  const probe = createEditor({ namespace: SCRIPT_DOC_NAMESPACE, nodes: [...SCRIPT_DOC_NODES], onError: rethrow });
  try {
    probe.parseEditorState(serialized);
    return serialized;
  } catch (e) {
    console.error('DocEditor: discarding unreadable seed doc', e);
    return undefined;
  }
}

/** Emits the seeded doc once on mount (OnChangePlugin skips the initial state). */
function InitialEmit({ onEmit }: { onEmit: (editor: LexicalEditor) => void }) {
  const [editor] = useLexicalComposerContext();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    // DocHygienePlugin cleans a restored draft in a history-merge update that
    // OnChangePlugin skips, so emit only once that pending update commits.
    editor.update(() => {}, { onUpdate: () => onEmit(editor) });
  }, [editor, onEmit]);
  return null;
}

export interface DocEditorProps {
  /** Seed doc (editing a version or restoring a draft). Read once on mount. */
  initialDoc?: ScriptDoc | null;
  /** Personalises the blank-editor scaffold placeholder. */
  creatorName?: string;
  /** Fires on every edit with the editor's native serialized document. */
  onChange: (doc: ScriptDoc) => void;
}

/** WYSIWYG script editor; emits the editor's native serialized Lexical doc. */
export function DocEditor({ initialDoc, creatorName = '', onChange }: DocEditorProps) {
  // Captured once — LexicalComposer reads initialConfig a single time.
  const initialConfig = useMemo(
    () => ({
      namespace: SCRIPT_DOC_NAMESPACE,
      nodes: [...SCRIPT_DOC_NODES],
      theme: scriptDocTheme,
      onError: rethrow,
      editorState: parseableSeedState(initialDoc),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const emit = useCallback((editor: LexicalEditor) => onChange(toDoc(editor.getEditorState())), [onChange]);
  const handleChange = useCallback((state: EditorState) => onChange(toDoc(state)), [onChange]);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Toolbar />
      <div className="relative">
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              ariaLabel={EDITOR_ARIA_LABEL}
              className={`min-h-[62vh] px-4 py-3 leading-relaxed text-foreground outline-none ${SCRIPT_TITLE_FIRST_CHILD_CLASSES}`}
            />
          }
          placeholder={<ScriptScaffold creatorName={creatorName} />}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>
      <HistoryPlugin />
      <ListPlugin />
      <ListTabIndentPlugin />
      <TabIndentationPlugin />
      <AutoFocusPlugin />
      <DocHygienePlugin />
      <ScriptShortcutsPlugin />
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      <InitialEmit onEmit={emit} />
    </LexicalComposer>
  );
}
